import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@apps/web/components/ui/sheet';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { SUPPORTED_SIDEBAR_OUTREACH_AGENT_MODELS } from '@apps/web/routes/_app/admin/organizations/sidebar_outreach_agent_models';
import { useAdminFlags } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AdminFlag, ApiError } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const STRING_FLAG_SELECT_OPTIONS: Readonly<
    Record<string, ReadonlyArray<string>>
> = {
    API_RATE_LIMIT_MODE: ['off', 'shadow', 'enforced'],
    V3_ORCHESTRATOR_MODEL: [
        'gpt-5.6-terra',
        'gpt-6.1-terra',
        'gpt-6-sol',
        'gpt-6-luna',
        'gpt-5.6-luna',
        'gpt-5.6-sol',
        'gpt-5.2',
        'gpt-5.4',
        'gpt-5.5',
    ],
    SIDEBAR_GENERAL_AGENT_EFFORT: ['low', 'medium', 'high'],
    SIDEBAR_GENERAL_AGENT_MODEL: SUPPORTED_SIDEBAR_OUTREACH_AGENT_MODELS,
    SIDEBAR_OUTREACH_AGENT_EFFORT: ['low', 'medium', 'high'],
    SIDEBAR_OUTREACH_AGENT_MODEL: SUPPORTED_SIDEBAR_OUTREACH_AGENT_MODELS,
};

function isDetailBody(body: unknown): body is { readonly detail: string } {
    return (
        typeof body === 'object' &&
        body !== null &&
        'detail' in body &&
        typeof (body as { readonly detail: unknown }).detail === 'string'
    );
}

export const Route = createFileRoute('/_app/admin/flags')({
    head: () => ({
        meta: [
            {
                title: 'Flags | Phoebe Admin',
            },
        ],
    }),
    component: AdminFlagsPage,
    validateSearch: z.object({
        flagName: z.string().optional(),
    }),
});

function getFlagTypeName(type: AdminFlag['type']) {
    switch (type) {
        case 'boolean':
            return 'Boolean';
        case 'integer':
            return 'Integer';
        case 'float':
            return 'Float';
        case 'string':
            return 'String';
        case 'nullable-string':
            return 'String';
        case 'list-uuid':
            return 'List of UUIDs';
        case 'list-string':
            return 'List of strings';
        default:
            assert.never(type);
    }
}

function FlagValueInput({
    flag,
    onChange,
}: {
    flag: AdminFlag;
    onChange: (value: FlagValueParseResult) => void;
}) {
    switch (flag.type) {
        case 'boolean':
            return (
                <BooleanValueInput
                    value={flag.current_value ?? flag.default_value}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        case 'integer':
            return (
                <IntValueInput
                    value={flag.current_value ?? flag.default_value}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        case 'float':
            return (
                <FloatValueInput
                    value={flag.current_value ?? flag.default_value}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        case 'string':
            return (
                <StringValueInput
                    flagName={flag.name}
                    value={flag.current_value ?? flag.default_value}
                    nullable={false}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        case 'nullable-string':
            return (
                <div className="space-y-1">
                    <StringValueInput
                        flagName={flag.name}
                        value={flag.current_value ?? flag.default_value ?? ''}
                        nullable={true}
                        onChange={(value) => {
                            onChange(value);
                        }}
                    />
                    <div className="text-muted-foreground text-xs">
                        (inherits AGENT_NOTE_CLASSIFIER_MODEL)
                    </div>
                </div>
            );
        case 'list-uuid':
            return (
                <ListUuidValueInput
                    value={flag.current_value ?? flag.default_value}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        case 'list-string':
            return (
                <ListStringValueInput
                    value={flag.current_value ?? flag.default_value}
                    onChange={(value) => {
                        onChange(value);
                    }}
                />
            );
        default:
            assert.never(flag);
    }
}

type FlagValueParseResult =
    | {
          readonly type: 'boolean';
          readonly valid: true;
          readonly value: boolean;
      }
    | { readonly type: 'integer'; readonly valid: true; readonly value: number }
    | { readonly type: 'float'; readonly valid: true; readonly value: number }
    | {
          readonly type: 'string';
          readonly valid: true;
          readonly value: string;
      }
    | {
          readonly type: 'nullable-string';
          readonly valid: true;
          readonly value: string | null;
      }
    | {
          readonly type: 'list-uuid';
          readonly valid: true;
          readonly value: Array<string>;
      }
    | {
          readonly type: 'list-string';
          readonly valid: true;
          readonly value: Array<string>;
      }
    | { readonly valid: false; readonly error: string };

function BooleanValueInput({
    value,
    onChange,
}: {
    value: boolean;
    onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(String(value));

    return (
        <select
            className="w-full rounded border p-2"
            value={editValue}
            onChange={(e) => {
                const value = e.target.value;
                setEditValue(value);
                onChange({
                    type: 'boolean',
                    valid: true,
                    value: value === 'true',
                });
            }}
        >
            <option value="true">True</option>
            <option value="false">False</option>
        </select>
    );
}

function IntValueInput({
    value,
    onChange,
}: {
    value: number;
    onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(String(value));

    return (
        <Input
            type="number"
            value={editValue}
            onChange={(e) => {
                const value = e.target.value;
                const parsedValue = parseInt(value);
                if (isNaN(parsedValue)) {
                    onChange({
                        valid: false,
                        error: 'Please enter a valid number',
                    });
                } else {
                    onChange({
                        type: 'integer',
                        valid: true,
                        value: parsedValue,
                    });
                }
                setEditValue(value);
            }}
        />
    );
}

function FloatValueInput({
    value,
    onChange,
}: {
    value: number;
    onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(String(value));

    return (
        <Input
            type="number"
            step="any"
            value={editValue}
            onChange={(e) => {
                const value = e.target.value;
                const parsedValue = parseFloat(value);
                if (isNaN(parsedValue)) {
                    onChange({
                        valid: false,
                        error: 'Please enter a valid number',
                    });
                } else {
                    onChange({
                        type: 'float',
                        valid: true,
                        value: parsedValue,
                    });
                }
                setEditValue(value);
            }}
        />
    );
}

function StringValueInput({
    flagName,
    value,
    nullable,
    onChange,
}: {
    readonly flagName: string;
    readonly value: string;
    readonly nullable: boolean;
    readonly onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(value);
    const options = STRING_FLAG_SELECT_OPTIONS[flagName];

    if (options) {
        const selectOptions = options.includes(editValue)
            ? options
            : [editValue, ...options];
        return (
            <select
                className="w-full rounded border p-2"
                value={editValue}
                onChange={(e) => {
                    const value = e.target.value;
                    if (nullable) {
                        onChange({
                            type: 'nullable-string',
                            valid: true,
                            value: value === '' ? null : value,
                        });
                    } else {
                        onChange({
                            type: 'string',
                            valid: true,
                            value,
                        });
                    }
                    setEditValue(value);
                }}
            >
                {selectOptions.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        );
    }

    return (
        <Input
            value={editValue}
            onChange={(e) => {
                const value = e.target.value;
                if (nullable) {
                    onChange({
                        type: 'nullable-string',
                        valid: true,
                        value: value === '' ? null : value,
                    });
                } else {
                    onChange({
                        type: 'string',
                        valid: true,
                        value,
                    });
                }
                setEditValue(value);
            }}
        />
    );
}

function ListUuidValueInput({
    value,
    onChange,
}: {
    value: Array<string>;
    onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(value.join(',\n'));
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useLayoutEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [editValue]);

    return (
        <Textarea
            ref={textareaRef}
            value={editValue}
            className="resize-y font-mono"
            onChange={(e) => {
                const value = e.target.value;
                setEditValue(value);

                const uuids = value
                    .split(',')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);

                const uuidRegex =
                    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                const isValid = uuids.every((uuid) => uuidRegex.test(uuid));

                if (!isValid) {
                    onChange({
                        valid: false,
                        error: 'All values must be valid UUIDs',
                    });
                } else {
                    onChange({ type: 'list-uuid', valid: true, value: uuids });
                }
            }}
        />
    );
}

function ListStringValueInput({
    value,
    onChange,
}: {
    value: Array<string>;
    onChange: (value: FlagValueParseResult) => void;
}) {
    const [editValue, setEditValue] = useState(value.join(',\n'));
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useLayoutEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [editValue]);

    return (
        <Textarea
            ref={textareaRef}
            value={editValue}
            className="resize-y font-mono"
            onChange={(e) => {
                const value = e.target.value;
                setEditValue(value);

                const strings = value
                    .split(',')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);

                onChange({ type: 'list-string', valid: true, value: strings });
            }}
        />
    );
}

function FlagTableRow({
    name,
    flag,
    onSelect,
    isSelected,
}: {
    name: string;
    flag: AdminFlag;
    onSelect: () => void;
    isSelected: boolean;
}) {
    const formattedValue = JSON.stringify(
        flag.current_value ?? flag.default_value,
        null,
        2,
    );

    return (
        <TableRow
            className={cn(
                'hover:bg-muted/50 cursor-pointer',
                isSelected && 'bg-muted',
            )}
            onClick={onSelect}
        >
            <TableCell>
                <code className="bg-muted rounded px-2 py-1 font-medium">
                    {name}
                </code>
            </TableCell>
            <TableCell>
                <div className="flex">
                    <pre className="bg-muted inline-block rounded px-2 py-1 text-sm whitespace-pre">
                        {formattedValue}
                    </pre>
                </div>
            </TableCell>
        </TableRow>
    );
}

function FlagDetailPanel({
    flag,
    onClose,
    mutate,
}: {
    flag: AdminFlag;
    onClose: () => void;
    mutate: () => void;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [stagedFlagValue, setStagedValue] =
        useState<FlagValueParseResult | null>(null);

    const handleUpdate = async () => {
        assert(stagedFlagValue?.valid, 'Staged value is not valid');

        setIsLoading(true);
        try {
            await getApiClient().put({
                url: '/admin/admin-flags/{flag_name}',
                pathParams: { flag_name: flag.name },
                body: stagedFlagValue,
            });

            toast.success('Flag updated successfully', {
                persistNotification: false,
            });
            setIsEditing(false);
        } catch (error) {
            const detail =
                error instanceof ApiError && isDetailBody(error.body)
                    ? error.body.detail
                    : null;
            toast.error(detail ?? 'Failed to update admin flag', {
                persistNotification: false,
            });
        }
        setIsLoading(false);
        mutate();
    };

    const isUpdateDisabled =
        isLoading ||
        !stagedFlagValue?.valid ||
        JSON.stringify(stagedFlagValue.value) ===
            JSON.stringify(flag.current_value);

    return (
        <Sheet open={true} onOpenChange={onClose}>
            <SheetContent className="w-[540px] max-w-none p-6">
                <SheetHeader>
                    <SheetTitle>{flag.name}</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                    <div>
                        <label className="text-sm font-medium">Type</label>
                        <div className="mt-1 text-sm">
                            {getFlagTypeName(flag.type)}
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium">
                            Description
                        </label>
                        <div className="mt-1 text-sm break-words whitespace-pre-wrap">
                            {flag.description || '-'}
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Value</label>
                        <div className="mt-2">
                            {isEditing ? (
                                <div className="space-y-2">
                                    {['list-string', 'list-uuid'].includes(
                                        flag.type,
                                    ) && (
                                        <div className="text-muted-foreground text-xs">
                                            Enter list of comma-separated
                                            values. Do not include quotes.
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <FlagValueInput
                                                flag={flag}
                                                onChange={setStagedValue}
                                            />
                                        </div>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={handleUpdate}
                                            disabled={isUpdateDisabled}
                                        >
                                            {isLoading ? (
                                                <Spinner />
                                            ) : (
                                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            )}
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setIsEditing(false)}
                                        >
                                            <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                                        </Button>
                                    </div>
                                    {stagedFlagValue?.valid === false && (
                                        <p className="text-sm text-red-500 dark:text-red-400">
                                            {stagedFlagValue?.error}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <code className="bg-muted flex-1 rounded px-2 py-1 whitespace-pre">
                                        {JSON.stringify(
                                            flag.current_value ??
                                                flag.default_value,
                                            null,
                                            2,
                                        )}
                                    </code>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium">
                            Last Update
                        </label>
                        <div className="text-muted-foreground mt-1 text-sm">
                            {flag.updated_at && flag.updated_by_email
                                ? `${new Date(flag.updated_at).toLocaleString()} by ${flag.updated_by_email}`
                                : '-'}
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function AdminFlagsPage() {
    const router = useRouter();
    const { flagName } = Route.useSearch();
    const { data: flags, error, isLoading, mutate } = useAdminFlags();

    if (isLoading) {
        return (
            <div className="flex justify-center p-6">
                <Spinner size="xl" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="text-red-500 dark:text-red-400">
                    Error loading admin flags {error.message}
                </div>
            </div>
        );
    }

    if (!flags) {
        return (
            <div className="p-6">
                <div className="text-muted-foreground">No flags found</div>
            </div>
        );
    }

    const selectedFlag = flags.find((f) => f.name === flagName);

    const handleSelectFlag = (flagName: string) => {
        router.navigate({
            to: '/admin/flags',
            search: (prev) => ({ ...prev, flagName }),
        });
    };

    const handleCloseFlag = () => {
        router.navigate({
            to: '/admin/flags',
            search: (prev) => ({ ...prev, flagName: undefined }),
        });
    };

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Admin Flags</h1>
                    <p className="text-muted-foreground mt-2 text-sm">
                        System flags to control application behavior. Changes
                        are immediately propagated to all services.
                    </p>
                </div>
            </div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Value</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {[...flags]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((flag) => (
                                <FlagTableRow
                                    key={flag.name}
                                    name={flag.name}
                                    flag={flag}
                                    onSelect={() => handleSelectFlag(flag.name)}
                                    isSelected={flagName === flag.name}
                                />
                            ))}
                    </TableBody>
                </Table>
            </div>
            {selectedFlag && (
                <FlagDetailPanel
                    key={selectedFlag.name}
                    flag={selectedFlag}
                    onClose={handleCloseFlag}
                    mutate={mutate}
                />
            )}
        </div>
    );
}
